import {
    FieldWrapper,
    Label,
} from '@/components/shared/components/TemplateManager/TemplateFormDialog.StyledComponents';
import { DecisionDetailStatusCode } from '@/components/pages/Applicant/Applicant.types';
import {
    ConditionDefinitionProduct,
    ConditionDefinitionTriggerInput,
    ConditionDefinitionTriggerOption,
} from '@/components/shared/api/endpoints/conditionDefinitions';
import { ExtraFieldsConfig } from '@/components/shared/components/TemplateManager/TemplateManager.types';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import {
    ASSIGNABLE_REVIEW_TEAMS,
    getReviewTeams,
    ReviewTeam,
    REVIEW_TEAM_LABEL,
} from '@/components/pages/Applicant/components/ApplicantConditions/conditionTemplates';
import { ReviewTeamPill } from '@/components/pages/Applicant/components/ApplicantConditions/ReviewTeamPill';
import { ConditionDefinitionTemplate } from './conditionDefinitionsAdapter';
import {
    formatConditionTriggers,
    MANUAL_TRIGGER_LABEL,
    PRODUCT_LABEL,
    STATUS_LABEL,
} from './conditionTriggers';
import { FieldHint, ReadOnlyValue } from './ManageTemplates.StyledComponents';

export interface ConditionExtraValues {
    defaultProducts: ConditionDefinitionProduct[];
    referenceProducts: ConditionDefinitionProduct[];
    reviewTeams: ReviewTeam[];
    /** A product with no entry is Manual. */
    triggers: Partial<Record<ConditionDefinitionProduct, DecisionDetailStatusCode>>;
}

const TRIGGER_PRODUCTS = [
    ConditionDefinitionProduct.Factoring,
    ConditionDefinitionProduct.LoadPay,
    ConditionDefinitionProduct.TcsFuel,
];

/** Coded and Auto-Comment templates get their triggers from backend code, so only the rest are configurable. */
export const hasConfigurableTriggers = (item: ConditionDefinitionTemplate | null): boolean =>
    item?.code == null && !item?.hasAutoComment;

// A product the template doesn't apply to has no dropdown, so its trigger is always Manual.
const appliesTo = (values: ConditionExtraValues, product: ConditionDefinitionProduct) =>
    values.defaultProducts.includes(product);

export const toTriggerInputs = (values: ConditionExtraValues): ConditionDefinitionTriggerInput[] =>
    TRIGGER_PRODUCTS.flatMap((product) => {
        const status = values.triggers[product];
        return status != null && appliesTo(values, product) ? [{ product, status }] : [];
    });

const PRODUCT_OPTIONS = [
    { label: 'Factoring', value: ConditionDefinitionProduct.Factoring },
    { label: 'LoadPay', value: ConditionDefinitionProduct.LoadPay },
    { label: 'TCS Fuel Card', value: ConditionDefinitionProduct.TcsFuel },
];

const REVIEW_TEAM_OPTIONS = ASSIGNABLE_REVIEW_TEAMS.map((team) => ({
    label: REVIEW_TEAM_LABEL[team],
    value: team,
}));

const getConditionExtraFieldsInitialValues = (
    item: ConditionDefinitionTemplate | null,
): ConditionExtraValues => ({
    defaultProducts: item?.defaultProducts ?? [],
    referenceProducts: item?.referenceProducts ?? [],
    reviewTeams: item?.reviewTeams ?? [],
    triggers: Object.fromEntries(
        (item?.triggers ?? []).map(({ product, status }) => [product, status]),
    ),
});

// Menus portal to body: with appendTo="self" the dialog's scroll area clips them, and MultiSelect (no position: relative) anchors to the dialog's top.
const renderConditionDetails = (editingItem: ConditionDefinitionTemplate) => (
    <FieldWrapper>
        <Label>Trigger</Label>
        <ReadOnlyValue data-testid="condition-form-trigger">
            {formatConditionTriggers(editingItem.triggers)}
        </ReadOnlyValue>
    </FieldWrapper>
);

const validateConditionExtraFields = (values: ConditionExtraValues): string | null =>
    values.defaultProducts.length === 0 ? 'Applies To needs at least one product.' : null;

const productLabels = (products: ConditionDefinitionProduct[]) =>
    PRODUCT_OPTIONS.filter((option) => products.includes(option.value))
        .map((option) => option.label)
        .join(', ') || 'None';

const triggerDropdownOptions = (statuses: DecisionDetailStatusCode[]) => [
    { label: MANUAL_TRIGGER_LABEL, value: null },
    ...statuses.map((status) => ({ label: STATUS_LABEL[status], value: status })),
];

const renderTriggerFields = (
    values: ConditionExtraValues,
    setValues: (next: ConditionExtraValues) => void,
    triggerOptions: ConditionDefinitionTriggerOption[] | undefined,
) =>
    TRIGGER_PRODUCTS.filter((product) => appliesTo(values, product)).map((product) => {
        const inputId = `condition-form-trigger-${ConditionDefinitionProduct[product].toLowerCase()}`;
        const statuses = triggerOptions?.find((option) => option.product === product)?.statuses;
        return (
            <FieldWrapper key={product}>
                <Label htmlFor={inputId}>{PRODUCT_LABEL[product]} Status trigger</Label>
                <Dropdown
                    inputId={inputId}
                    data-testid={inputId}
                    value={values.triggers[product] ?? null}
                    options={triggerDropdownOptions(statuses ?? [])}
                    // Without optionValue, PrimeReact returns the whole option when its value is empty, so Manual (null) would arrive as an object.
                    optionValue="value"
                    onChange={(e) =>
                        setValues({
                            ...values,
                            triggers: { ...values.triggers, [product]: e.value ?? undefined },
                        })
                    }
                    placeholder={MANUAL_TRIGGER_LABEL}
                    // Until the options load, a stored trigger would wrongly read as Manual.
                    disabled={!triggerOptions}
                    appendTo={document.body}
                />
            </FieldWrapper>
        );
    });

const renderConditionExtraFields = (
    values: ConditionExtraValues,
    setValues: (next: ConditionExtraValues) => void,
    editingItem: ConditionDefinitionTemplate | null,
    canEditTriggers: boolean,
    triggerOptions: ConditionDefinitionTriggerOption[] | undefined,
) => (
    <>
        <FieldWrapper>
            <Label htmlFor="condition-form-default-products">Applies To</Label>
            <MultiSelect
                inputId="condition-form-default-products"
                data-testid="condition-form-default-products"
                value={values.defaultProducts}
                options={PRODUCT_OPTIONS}
                onChange={(e) => setValues({ ...values, defaultProducts: e.value })}
                placeholder="Select products…"
                appendTo={document.body}
            />
        </FieldWrapper>

        {/* Only the backend's LoadPay association reads this, and only for coded rows, so it isn't editable. */}
        {editingItem?.code != null ? (
            <FieldWrapper>
                <Label>Reference Products</Label>
                <ReadOnlyValue data-testid="condition-form-reference-products">
                    {productLabels(values.referenceProducts)}
                </ReadOnlyValue>
                <FieldHint>
                    Set by the system. If LoadPay is listed, this task is also added to LoadPay when
                    LoadPay goes to manual review.
                </FieldHint>
            </FieldWrapper>
        ) : null}

        {canEditTriggers ? renderTriggerFields(values, setValues, triggerOptions) : null}

        <FieldWrapper>
            <Label htmlFor="condition-form-review-team">Review Teams (optional)</Label>
            <MultiSelect
                inputId="condition-form-review-team"
                data-testid="condition-form-review-team"
                value={values.reviewTeams}
                options={REVIEW_TEAM_OPTIONS}
                onChange={(e) =>
                    setValues({ ...values, reviewTeams: getReviewTeams(e.value as ReviewTeam[]) })
                }
                placeholder="Select review teams…"
                showSelectAll={false}
                showClear
                appendTo={document.body}
                itemTemplate={(option: { value: ReviewTeam }) => (
                    <ReviewTeamPill team={option.value} isCompact={true} />
                )}
            />
        </FieldWrapper>
    </>
);

export const buildConditionExtraFields = (
    canEditTriggers: (item: ConditionDefinitionTemplate | null) => boolean,
    triggerOptions: ConditionDefinitionTriggerOption[] | undefined,
): ExtraFieldsConfig<ConditionDefinitionTemplate, ConditionExtraValues> => ({
    getInitialValues: getConditionExtraFieldsInitialValues,
    render: (values, setValues, editingItem) =>
        renderConditionExtraFields(
            values,
            setValues,
            editingItem,
            canEditTriggers(editingItem),
            triggerOptions,
        ),
    validate: validateConditionExtraFields,
    renderDetails: (editingItem) =>
        canEditTriggers(editingItem) ? null : renderConditionDetails(editingItem),
});
